import { FC } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  ChartData,
} from 'chart.js';
import { Skeleton } from '@mui/material';

import { prefersDarkMode } from '@carcareers/theme';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler);

export interface LineChartProps {
  xAxisLabels: string[];
  data: number[];
  dataLabel: string;
  secondaryData?: number[];
  secondaryDataLabel?: string;
  isLoading?: boolean;
}

export const LineChart: FC<LineChartProps> = ({
  data,
  xAxisLabels,
  dataLabel,
  secondaryData,
  secondaryDataLabel,
  isLoading,
}) => {
  // Determine which dataset has lower values on average
  const primaryAvg = data.reduce((a, b) => a + b, 0) / data.length;
  const secondaryAvg = secondaryData ? secondaryData.reduce((a, b) => a + b, 0) / secondaryData.length : Infinity;

  const datasets = [
    {
      label: dataLabel,
      data,
      borderColor: '#42A5f5',
      backgroundColor: (context: any) => {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        if (!chartArea) return;

        const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        gradient.addColorStop(0, prefersDarkMode ? 'rgba(0,0,0,0)' : 'rgba(255,255,255,0.5)');
        gradient.addColorStop(1, 'rgba(66,165,245,0.5)');
        return gradient;
      },
      fill: true,
      tension: 0.4,
      borderWidth: 3,
      order: primaryAvg <= secondaryAvg ? 1 : 2,
      pointRadius: (context: any) => {
        const index = context.dataIndex;
        return index === data.length - 1 ? 3 : 0;
      },
      pointBackgroundColor: '#42A5f5',
    },
    ...(secondaryData
      ? [
          {
            label: secondaryDataLabel,
            data: secondaryData,
            borderColor: '#FFB74D',
            backgroundColor: (context: any) => {
              const chart = context.chart;
              const { ctx, chartArea } = chart;
              if (!chartArea) return;

              const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
              gradient.addColorStop(0, prefersDarkMode ? 'rgba(0, 0, 0, 0)' : 'rgba(255,255,255,0.5)');
              gradient.addColorStop(1, 'rgba(255,183,77,0.5)');
              return gradient;
            },
            fill: true,
            tension: 0.4,
            borderWidth: 3,
            order: primaryAvg > secondaryAvg ? 1 : 2,
            pointRadius: (context: any) => {
              const index = context.dataIndex;
              return index === secondaryData.length - 1 ? 3 : 0;
            },
            pointBackgroundColor: '#FFB74D',
          },
        ]
      : []),
  ];

  const chartData: ChartData<'line'> = {
    labels: xAxisLabels,
    datasets,
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: secondaryData ? true : false,
        position: 'top' as const,
      },
      tooltip: {
        mode: 'nearest' as const,
        intersect: false,
        backgroundColor: 'secondary.main',
        color: 'secondary.contrastText',
        borderRadius: 4,
        padding: 10,
        displayColors: false,
      },
      annotation: {
        annotations: {
          todayLine: {
            type: 'line',
            xMin: xAxisLabels.length - 1,
            xMax: xAxisLabels.length - 1,
            borderColor: 'secondary.main',
            borderWidth: 1,
            borderDash: [5, 5],
            label: {
              content: 'Today',
              enabled: true,
              position: 'bottom',
            },
          },
        },
      },
    },
    scales: {
      y: {
        display: false,
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  return isLoading ? <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} /> : <Line options={chartOptions} data={chartData} />;
};

export default LineChart;
